import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useMinimapSize } from '../../../SprintDetail/FlowchartView/components/Minimap/hooks/useMinimapSize';
import { calculateMinimapDimensions } from '../../../SprintDetail/FlowchartView/components/Minimap/utils/calculateMinimapDimensions';
import '../../../SprintDetail/FlowchartView/components/Minimap/Minimap.css';
import './AtlasMinimap.css';
import { FolderIcon, UnfiledIcon } from '../../../Sidebar/icons';
import { isAtlasUnfiledId } from '../constants';
import { ATLAS_ICON } from '../layoutTree';

const MAP_PAD = 80;

const toMapBounds = (bounds) => {
  if (
    !bounds ||
    !isFinite(bounds.maxX) ||
    !isFinite(bounds.minX) ||
    bounds.maxX <= bounds.minX
  ) {
    return { minX: 0, minY: 0, maxX: 400, maxY: 300, width: 400, height: 300 };
  }
  const minX = bounds.minX - MAP_PAD;
  const minY = bounds.minY - MAP_PAD;
  const maxX = bounds.maxX + MAP_PAD;
  const maxY = bounds.maxY + MAP_PAD;
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};

const AtlasMinimap = ({
  nodes = [],
  edges = [],
  bounds,
  pan,
  zoom,
  containerRef,
  onPanChange,
  onPanChangeEnd,
  liveApplyRef,
}) => {
  const minimapRef = useRef(null);
  const viewportRef = useRef(null);
  const containerSizeRef = useRef({ width: 0, height: 0 });
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const grabRef = useRef(null);
  const draggingRef = useRef(false);

  const mapBounds = useMemo(() => toMapBounds(bounds), [bounds]);

  const { maxDimensions, isResizing, handleResizeStart } = useMinimapSize(
    containerRef,
    mapBounds.width,
    mapBounds.height,
    null,
    null,
    'atlas-docs'
  );

  const { minimapWidth, minimapHeight, scale } = useMemo(
    () => calculateMinimapDimensions(mapBounds, maxDimensions),
    [mapBounds, maxDimensions]
  );

  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const updateViewport = useCallback(
    (panX, panY, currentZoom) => {
      const el = viewportRef.current;
      const size = containerSizeRef.current;
      if (!el || !size.width) return;
      const z = currentZoom || 1;
      const viewX = (-panX / z - mapBounds.minX) * scale;
      const viewY = (-panY / z - mapBounds.minY) * scale;
      el.style.transform = `translate(${viewX}px, ${viewY}px)`;
      el.style.width = `${(size.width / z) * scale}px`;
      el.style.height = `${(size.height / z) * scale}px`;
    },
    [mapBounds.minX, mapBounds.minY, scale]
  );

  useLayoutEffect(() => {
    if (liveApplyRef) liveApplyRef.current = updateViewport;
    return () => {
      if (liveApplyRef) liveApplyRef.current = null;
    };
  }, [liveApplyRef, updateViewport]);

  useLayoutEffect(() => {
    panRef.current = pan;
    zoomRef.current = zoom;
    const host = containerRef?.current;
    if (host) {
      const r = host.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        containerSizeRef.current = { width: r.width, height: r.height };
      }
    }
    updateViewport(pan.x, pan.y, zoom);
  }, [pan, zoom, containerRef, updateViewport]);

  useLayoutEffect(() => {
    const host = containerRef?.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const r = host.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        containerSizeRef.current = { width: r.width, height: r.height };
        updateViewport(panRef.current.x, panRef.current.y, zoomRef.current);
      }
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [containerRef, updateViewport]);

  const panFromEvent = useCallback(
    (e) => {
      const map = minimapRef.current;
      if (!map) return null;
      const rect = map.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(e.clientX - rect.left, minimapWidth));
      const clickY = Math.max(0, Math.min(e.clientY - rect.top, minimapHeight));
      const z = zoomRef.current || 1;
      const size = containerSizeRef.current;
      const grab = grabRef.current;
      if (grab && size.width > 0) {
        return {
          x: -(mapBounds.minX + (clickX - grab.offX) / scale) * z,
          y: -(mapBounds.minY + (clickY - grab.offY) / scale) * z,
        };
      }
      const worldX = clickX / scale + mapBounds.minX;
      const worldY = clickY / scale + mapBounds.minY;
      return {
        x: -(worldX * z - size.width / 2),
        y: -(worldY * z - size.height / 2),
      };
    },
    [mapBounds.minX, mapBounds.minY, minimapWidth, minimapHeight, scale]
  );

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = true;
      minimapRef.current?.classList.add('flowchart-minimap__canvas--dragging');

      const map = minimapRef.current;
      const size = containerSizeRef.current;
      const z = zoomRef.current || 1;
      const p = panRef.current;
      grabRef.current = null;
      if (map && size.width > 0) {
        const mr = map.getBoundingClientRect();
        const cx = Math.max(0, Math.min(e.clientX - mr.left, minimapWidth));
        const cy = Math.max(0, Math.min(e.clientY - mr.top, minimapHeight));
        const vx = (-p.x / z - mapBounds.minX) * scale;
        const vy = (-p.y / z - mapBounds.minY) * scale;
        const vw = (size.width / z) * scale;
        const vh = (size.height / z) * scale;
        if (cx >= vx && cx <= vx + vw && cy >= vy && cy <= vy + vh) {
          grabRef.current = { offX: cx - vx, offY: cy - vy };
        }
      }

      const apply = (ev) => {
        const next = panFromEvent(ev);
        if (next) onPanChange?.(next);
      };
      apply(e);

      const onMove = (ev) => {
        if (draggingRef.current) apply(ev);
      };
      const onUp = () => {
        draggingRef.current = false;
        grabRef.current = null;
        minimapRef.current?.classList.remove('flowchart-minimap__canvas--dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp, true);
        onPanChangeEnd?.();
      };
      document.addEventListener('mousemove', onMove, { passive: true });
      document.addEventListener('mouseup', onUp, { capture: true });
    },
    [mapBounds.minX, mapBounds.minY, minimapWidth, minimapHeight, scale, onPanChange, onPanChangeEnd, panFromEvent]
  );

  if (nodes.length === 0) return null;

  return (
    <div className="atlas-minimap-wrapper">
      <div className={`flowchart-minimap ${isResizing ? 'flowchart-minimap--resizing' : ''}`}>
        <span className="flowchart-minimap__frame" aria-hidden="true" />
        <div
          className="flowchart-minimap__resize-handle flowchart-minimap__resize-handle--left"
          onMouseDown={(e) => handleResizeStart('left', e)}
        />
        <div
          className="flowchart-minimap__resize-handle flowchart-minimap__resize-handle--top"
          onMouseDown={(e) => handleResizeStart('top', e)}
        />
        <div
          ref={minimapRef}
          className="flowchart-minimap__canvas"
          style={{ width: minimapWidth, height: minimapHeight }}
          onMouseDown={handleMouseDown}
        >
          <svg
            className="flowchart-minimap__edges"
            width={minimapWidth}
            height={minimapHeight}
            aria-hidden
          >
            {edges.map((edge) => {
              const a = nodeById.get(edge.source);
              const b = nodeById.get(edge.target);
              if (!a || !b) return null;
              const x1 = (a.x + ATLAS_ICON.centerX - mapBounds.minX) * scale;
              const y1 = (a.y + ATLAS_ICON.padY + ATLAS_ICON.height * ATLAS_ICON.glyphBotClosed - mapBounds.minY) * scale;
              const x2 = (b.x + ATLAS_ICON.centerX - mapBounds.minX) * scale;
              const y2 = (b.y + ATLAS_ICON.padY + ATLAS_ICON.height * ATLAS_ICON.glyphTopClosed - mapBounds.minY) * scale;
              return (
                <line
                  key={edge.id}
                  className="flowchart-minimap__edge"
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                />
              );
            })}
          </svg>
          {nodes.map((node) => {
            const unfiled = isAtlasUnfiledId(node.id) || node.kind === 'unfiled';
            const iconPx = Math.min(18, Math.max(9, Math.round(ATLAS_ICON.width * scale)));
            const x = (node.x + ATLAS_ICON.padX - mapBounds.minX) * scale;
            const y = (node.y + ATLAS_ICON.padY - mapBounds.minY) * scale;
            return (
              <div
                key={node.id}
                className={`atlas-minimap__icon${unfiled ? ' atlas-minimap__icon--unfiled' : ''}`}
                style={{ transform: `translate(${x}px, ${y}px)` }}
              >
                {unfiled ? <UnfiledIcon size={iconPx} /> : <FolderIcon size={iconPx} />}
              </div>
            );
          })}
          <div ref={viewportRef} className="flowchart-minimap__viewport" />
        </div>
      </div>
    </div>
  );
};

export default AtlasMinimap;
